/**
 * ObserverGenerateReport
 * Generate "Day's Insights" from an uploaded audio file, an uploaded video file,
 * or a pasted transcript.
 *
 * Modes:
 *   • audio      → POST multipart/form-data → /api/observer/generate-call-report-upload → poll job status
 *   • video      → POST multipart/form-data → /api/observer/generate-call-report-upload (source_type=video_upload) → poll job status
 *   • transcript → POST query params         → /api/observer/generate-call-report-transcript
 *
 * Audio/video uploads return a job_id immediately — transcription + AI analysis run in
 * the background and can take several minutes for large files, longer than the hosting
 * platform's proxy read timeout allows for a single blocking request. The frontend polls
 * /api/observer/generate-call-report-status/:jobId until the job completes or fails.
 *
 * On success, navigates to /observer/report-detail/:reportId.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import {
  Sparkles, Loader2, Wand2, Upload, Video,
  FileAudio, FileVideo, X, FileText, AlertCircle,
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_AUDIO_BYTES = 50 * 1024 * 1024;
const MAX_VIDEO_BYTES = 200 * 1024 * 1024;
const ACCEPTED_AUDIO_EXTS = ['mp3', 'wav', 'm4a', 'aac', 'ogg', 'flac', 'webm', 'mp4', 'opus', 'amr', '3gp'];
const ACCEPTED_VIDEO_EXTS = ['mp4', 'webm', 'mov', 'mkv', 'm4v', 'avi', '3gp', 'wmv'];
const MIN_TRANSCRIPT_CHARS = 100;

const formatBytes = (b) => {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

const ModeBtn = ({ active, onClick, disabled, icon: Icon, label, testId }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`px-3 py-1.5 rounded-full text-xs font-medium transition flex items-center gap-1.5 ${
      active
        ? 'bg-violet-600 text-white shadow-sm'
        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
    }`}
    data-testid={testId}
  >
    <Icon className="w-3.5 h-3.5" />
    {label}
  </button>
);

const GENDER_MAP = { male: 'male', female: 'female', boy: 'male', girl: 'female' };

const ObserverGenerateReport = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [mode, setMode] = useState('audio'); // 'audio' | 'video' | 'transcript'
  const [audioFile, setAudioFile] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [transcriptText, setTranscriptText] = useState('');
  const [childName, setChildName] = useState('');
  const [gender, setGender] = useState('female');
  const [busy, setBusy] = useState(false);
  const [assignedChildren, setAssignedChildren] = useState([]);
  const [childrenLoading, setChildrenLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('observer_token');
    if (!token) { setChildrenLoading(false); return; }
    axios.get(`${BACKEND_URL}/api/observer/children?token=${token}`)
      .then(res => {
        const list = res.data?.children || [];
        setAssignedChildren(list);
        if (list.length === 1) {
          setChildName(list[0].name);
          const g = GENDER_MAP[(list[0].gender || '').toLowerCase()] || 'female';
          setGender(g);
        }
      })
      .catch(() => {/* silent — falls back to empty list */})
      .finally(() => setChildrenLoading(false));
  }, []);

  const handleChildSelect = (e) => {
    const name = e.target.value;
    setChildName(name);
    const child = assignedChildren.find(c => c.name === name);
    if (child) {
      const g = GENDER_MAP[(child.gender || '').toLowerCase()] || 'female';
      setGender(g);
    }
  };

  const handleFilePick = (kind) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const exts = kind === 'video' ? ACCEPTED_VIDEO_EXTS : ACCEPTED_AUDIO_EXTS;
    const maxBytes = kind === 'video' ? MAX_VIDEO_BYTES : MAX_AUDIO_BYTES;
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!exts.includes(ext)) {
      toast({
        title: 'Unsupported file type',
        description: `Allowed: ${exts.join(', ')}`,
        variant: 'destructive',
      });
      e.target.value = '';
      return;
    }
    if (file.size > maxBytes) {
      toast({
        title: 'File too large',
        description: `Max ${formatBytes(maxBytes)}. This file is ${formatBytes(file.size)}.`,
        variant: 'destructive',
      });
      e.target.value = '';
      return;
    }
    if (kind === 'video') setVideoFile(file);
    else setAudioFile(file);
  };

  const reset = () => {
    setAudioFile(null);
    setVideoFile(null);
    setTranscriptText('');
  };

  const POLL_INTERVAL_MS = 4000;
  const MAX_POLL_MS = 10 * 60 * 1000; // give up after 10 minutes

  // Audio/video uploads process in the background (transcription + AI analysis can
  // take several minutes for large files) — the server hands back a job_id right away
  // and we poll for completion instead of holding one long HTTP request open, since
  // the hosting platform's reverse proxy kills any single request past ~120s.
  const pollJobStatus = async (token, jobId) => {
    const startedAt = Date.now();
    let consecutiveErrors = 0;
    while (Date.now() - startedAt < MAX_POLL_MS) {
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
      try {
        const res = await axios.get(
          `${BACKEND_URL}/api/observer/generate-call-report-status/${jobId}`,
          { params: { token }, timeout: 15000 },
        );
        consecutiveErrors = 0;
        const job = res.data;
        if (job.status === 'completed') return job;
        if (job.status === 'failed') throw new Error(job.error || 'Report generation failed');
        // else still "processing" — keep polling
      } catch (e) {
        if (!e.isAxiosError) throw e; // job explicitly failed — surface it now
        if (e.response && e.response.status === 404) throw e; // job vanished — unrecoverable
        consecutiveErrors += 1;
        if (consecutiveErrors >= 5) throw e; // repeated network errors — give up
      }
    }
    throw new Error('Report generation is taking longer than expected. Check My Reports in a few minutes.');
  };

  const handleGenerate = async () => {
    if (!childName.trim()) {
      toast({ title: 'Please select a child', variant: 'destructive' });
      return;
    }
    if (mode === 'audio' && !audioFile) {
      toast({ title: 'Pick an audio file first', variant: 'destructive' });
      return;
    }
    if (mode === 'video' && !videoFile) {
      toast({ title: 'Pick a video file first', variant: 'destructive' });
      return;
    }
    if (mode === 'transcript' && transcriptText.trim().length < MIN_TRANSCRIPT_CHARS) {
      toast({ title: 'Transcript too short', description: 'Paste the full session transcript — at least a few sentences of conversation.', variant: 'destructive' });
      return;
    }

    setBusy(true);
    try {
      const token = localStorage.getItem('observer_token');
      let data;

      if (mode === 'audio') {
        const fd = new FormData();
        fd.append('token', token);
        fd.append('audio_file', audioFile);
        fd.append('child_name', childName.trim());
        fd.append('gender', gender);
        const res = await axios.post(
          `${BACKEND_URL}/api/observer/generate-call-report-upload`,
          fd,
          { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 },
        );
        data = await pollJobStatus(token, res.data.job_id);
      } else if (mode === 'video') {
        const fd = new FormData();
        fd.append('token', token);
        fd.append('audio_file', videoFile);
        fd.append('child_name', childName.trim());
        fd.append('gender', gender);
        fd.append('source_type', 'video_upload');
        const res = await axios.post(
          `${BACKEND_URL}/api/observer/generate-call-report-upload`,
          fd,
          { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 },
        );
        data = await pollJobStatus(token, res.data.job_id);
      } else {
        // transcript mode — JSON body so transcript length is never URL-limited
        const res = await axios.post(
          `${BACKEND_URL}/api/observer/generate-call-report-transcript`,
          {
            token,
            child_name: childName.trim(),
            gender,
            transcript: transcriptText.trim(),
          },
          { timeout: 180000 },
        );
        data = res.data;
      }

      toast({
        title: 'Report ready',
        description: `Engagement Score ${data.engagement_score?.score}/100 · ${data.engagement_score?.label}`,
      });
      navigate(`/observer/report-detail/${data.report_id}`);
    } catch (e) {
      console.error(e);
      toast({
        title: 'Generation failed',
        description: e?.response?.data?.detail || e.message,
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  const transcriptLen = transcriptText.trim().length;
  const transcriptValid = transcriptLen >= MIN_TRANSCRIPT_CHARS;

  return (
    <Card className="border-violet-200" data-testid="observer-generate-report">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-violet-700">
          <Sparkles className="w-5 h-5" /> Generate Day's Insights
        </CardTitle>
        <p className="text-sm text-slate-500">
          Upload an audio file, upload a video file, or paste the raw session transcript directly.
          All three produce the same structured report with Engagement Score → Principal Review → Parent's Daily Insights.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Mode toggle */}
        <div className="flex gap-2 flex-wrap" data-testid="generate-report-mode-toggle">
          <ModeBtn
            active={mode === 'audio'}
            onClick={() => { setMode('audio'); reset(); }}
            disabled={busy}
            icon={Upload}
            label="Upload Audio"
            testId="generate-mode-audio"
          />
          <ModeBtn
            active={mode === 'video'}
            onClick={() => { setMode('video'); reset(); }}
            disabled={busy}
            icon={Video}
            label="Upload Video"
            testId="generate-mode-video"
          />
          <ModeBtn
            active={mode === 'transcript'}
            onClick={() => { setMode('transcript'); reset(); }}
            disabled={busy}
            icon={FileText}
            label="Enter Transcript"
            testId="generate-mode-transcript"
          />
        </div>

        {/* Input area */}
        {mode === 'audio' && (
          <div>
            <Label>Audio File</Label>
            {!audioFile ? (
              <label
                htmlFor="audio-upload"
                className="mt-1 flex flex-col items-center justify-center gap-1 px-4 py-8 border-2 border-dashed border-slate-300 rounded-md bg-slate-50 cursor-pointer hover:border-violet-400 hover:bg-violet-50 transition"
                data-testid="audio-file-dropzone"
              >
                <Upload className="w-6 h-6 text-slate-400" />
                <span className="text-sm font-medium text-slate-700">Click to choose an audio file</span>
                <span className="text-xs text-slate-500">
                  {ACCEPTED_AUDIO_EXTS.join(', ')} · max {formatBytes(MAX_AUDIO_BYTES)}
                </span>
                <input
                  id="audio-upload"
                  type="file"
                  accept="audio/*,video/mp4,.mp3,.wav,.m4a,.aac,.ogg,.flac,.webm,.mp4,.opus,.amr,.3gp"
                  className="hidden"
                  onChange={handleFilePick('audio')}
                  disabled={busy}
                  data-testid="audio-file-input"
                />
              </label>
            ) : (
              <div
                className="mt-1 flex items-center justify-between gap-3 px-4 py-3 bg-violet-50 border border-violet-200 rounded-md"
                data-testid="audio-file-selected"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <FileAudio className="w-5 h-5 text-violet-600 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{audioFile.name}</p>
                    <p className="text-xs text-slate-500">{formatBytes(audioFile.size)}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setAudioFile(null)}
                  disabled={busy}
                  className="p-1 rounded-full hover:bg-violet-100 text-slate-500 hover:text-slate-700 disabled:opacity-50"
                  data-testid="audio-file-clear"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        )}

        {mode === 'video' && (
          <div>
            <Label>Video File</Label>
            {!videoFile ? (
              <label
                htmlFor="video-upload"
                className="mt-1 flex flex-col items-center justify-center gap-1 px-4 py-8 border-2 border-dashed border-slate-300 rounded-md bg-slate-50 cursor-pointer hover:border-violet-400 hover:bg-violet-50 transition"
                data-testid="video-file-dropzone"
              >
                <Video className="w-6 h-6 text-slate-400" />
                <span className="text-sm font-medium text-slate-700">Click to choose a video file</span>
                <span className="text-xs text-slate-500">
                  {ACCEPTED_VIDEO_EXTS.join(', ')} · max {formatBytes(MAX_VIDEO_BYTES)}
                </span>
                <input
                  id="video-upload"
                  type="file"
                  accept="video/*,.mp4,.webm,.mov,.mkv,.m4v,.avi,.3gp,.wmv"
                  className="hidden"
                  onChange={handleFilePick('video')}
                  disabled={busy}
                  data-testid="video-file-input"
                />
              </label>
            ) : (
              <div
                className="mt-1 flex items-center justify-between gap-3 px-4 py-3 bg-violet-50 border border-violet-200 rounded-md"
                data-testid="video-file-selected"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <FileVideo className="w-5 h-5 text-violet-600 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{videoFile.name}</p>
                    <p className="text-xs text-slate-500">{formatBytes(videoFile.size)}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setVideoFile(null)}
                  disabled={busy}
                  className="p-1 rounded-full hover:bg-violet-100 text-slate-500 hover:text-slate-700 disabled:opacity-50"
                  data-testid="video-file-clear"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
            <p className="mt-2 text-xs text-slate-500">
              We extract the audio track from your video to generate the report. The video is kept so the principal can review it later.
            </p>
          </div>
        )}

        {mode === 'transcript' && (
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label htmlFor="transcript-input">Session Transcript</Label>
              <span className={`text-xs ${transcriptValid ? 'text-emerald-600' : 'text-slate-400'}`}>
                {transcriptLen} chars{transcriptLen > 0 && !transcriptValid ? ` (need ${MIN_TRANSCRIPT_CHARS}+)` : ''}
              </span>
            </div>
            <textarea
              id="transcript-input"
              data-testid="transcript-input"
              rows={10}
              placeholder={`Paste the raw session transcript here — exactly as received from the call recording or Whisper output. No need to label speaker turns.\n\nExample:\nOkay, how was your day today? Good. Just good? What did you do at school? We had art class and I painted a dog. Oh, that's lovely! Did you name the dog? Yeah, I called him Bruno. My real dog is also named Bruno...`}
              value={transcriptText}
              onChange={(e) => setTranscriptText(e.target.value)}
              disabled={busy}
              className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 rounded-md bg-white resize-y focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent disabled:opacity-60 font-mono leading-relaxed"
            />
            <div className="mt-2 flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>
                Paste the transcript exactly as-is — raw Whisper output, no speaker labels needed. The AI
                separates observer questions from the child's responses automatically. Names in the transcript
                are ignored; only the child's name you enter below is used in the report.
              </span>
            </div>
          </div>
        )}

        {/* Child details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="child-select">Child</Label>
            {childrenLoading ? (
              <div className="flex items-center gap-2 h-10 px-3 border rounded-md bg-slate-50 text-sm text-slate-400">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
              </div>
            ) : assignedChildren.length === 0 ? (
              <div
                className="flex items-start gap-2 h-auto min-h-10 px-3 py-2 border border-amber-200 rounded-md bg-amber-50 text-xs text-amber-800"
                data-testid="no-children-assigned"
              >
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <div>
                  <div className="font-semibold">No students assigned yet</div>
                  <div className="mt-0.5">
                    Ask the admin to assign a student to your account before generating a report.
                  </div>
                </div>
              </div>
            ) : (
              <select
                id="child-select"
                data-testid="child-name-input"
                className="w-full border rounded-md h-10 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent disabled:opacity-60"
                value={childName}
                onChange={handleChildSelect}
                disabled={busy}
              >
                <option value="">— Select a child —</option>
                {assignedChildren.map(child => (
                  <option key={child.id || child.name} value={child.name}>
                    {child.name}{child.grade ? ` · ${child.grade}` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div>
            <Label htmlFor="gender">Gender</Label>
            <select
              id="gender"
              data-testid="gender-select"
              className="w-full border rounded-md h-10 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent disabled:opacity-60"
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              disabled={busy}
            >
              <option value="female">Female (she/her)</option>
              <option value="male">Male (he/him)</option>
              <option value="other">Other (they/them)</option>
            </select>
          </div>
        </div>

        <Button
          onClick={handleGenerate}
          disabled={busy || !childName.trim() || (!childrenLoading && assignedChildren.length === 0)}
          className="w-full md:w-auto bg-violet-600 hover:bg-violet-700"
          data-testid="generate-report-btn"
        >
          {busy ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {mode === 'transcript' ? 'Analysing transcript…' : 'Processing — can take a few minutes for larger files…'}
            </>
          ) : (
            <><Wand2 className="w-4 h-4 mr-2" /> Generate Report</>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};

export default ObserverGenerateReport;
